import { ApplicationsTabEnumEnum } from 'modules/applications/enums/ApplicationsTabEnum.enum';
import { UserBadge } from 'modules/layouts/LoggedLayout/components/Navbar/subcomponents/UserBadge';
import { useModal } from 'modules/modal/hooks/useModal';
import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { appConfig } from 'shared/appConfig';
import { IconButton } from 'shared/components/IconButton/IconButton';
import { useApplicationCountStore } from 'shared/hooks/stores/useApplicationCountStore';
import { useExpertProfileStore } from 'shared/hooks/stores/useExpertProfileStore';
import { useHubStateStore } from 'shared/hooks/stores/useHubStateStore';
import { useApplicationSearchParam } from 'shared/hooks/useApplicationSearchParam';
import { useBreakpoint } from 'shared/hooks/useBreakpoint';
import { useSitemap } from 'shared/hooks/useSitemap';
import { useSkipTransitionOnChange } from 'shared/hooks/useSkipTransitionOnChange';
import {
  BusinessIcon,
  CrossBigIcon,
  GearIcon,
  HeartIcon,
  MultiDocumentIcon,
  UserIcon,
} from 'shared/icons';
import { ConnectisLogo } from 'shared/icons/ConnectisLogo';
import { ConnectisSmallLogo } from 'shared/icons/ConnectisSmallLogo';
import { mergeClasses } from 'shared/utils/mergeClasses';
import { useShallow } from 'zustand/react/shallow';
import { SignOutModal } from '../SignOutModal';
import ellipseCenterSrc from './assets/ellipse-center.svg';
import ellipseTopSrc from './assets/ellipse-top.svg';
import { Menu, MenuPath } from './subcomponents/Menu';
import { SignOutButton } from './subcomponents/SignOutButton';

interface NavbarProps {
  open: boolean;
  onClose: () => void;
}

export const Navbar: FC<NavbarProps> = memo(({ open, onClose }) => {
  const { t } = useTranslation();
  const sitemap = useSitemap();
  const { openModal } = useModal();
  const isMobile = useBreakpoint('lg');
  const applyTransition = useSkipTransitionOnChange(isMobile);
  const { isApplicationParamPresent } = useApplicationSearchParam();
  const unviewedApplicationCount = useApplicationCountStore((store) => store.count);

  const { firstName, lastName, jobPositionId } = useExpertProfileStore(
    useShallow(({ expertProfile }) => ({
      firstName: expertProfile?.firstName || '',
      lastName: expertProfile?.lastName || '',
      jobPositionId: expertProfile?.jobPositionId || '',
    })),
  );
  const onboardingFinishedAt = useHubStateStore((state) => state.finishedAt);

  const paths: MenuPath[] = useMemo(
    () => [
      {
        href: sitemap.offers,
        label: t('pageTitles:offers'),
        icon: BusinessIcon,
        activePaths: isApplicationParamPresent
          ? [sitemap.offers, sitemap.notifications, sitemap.observed]
          : [sitemap.offers, sitemap.jobOrder(''), sitemap.notifications, sitemap.observed],
      },
      {
        href: sitemap.applications(ApplicationsTabEnumEnum.Current),
        label: t('pageTitles:applications'),
        icon: MultiDocumentIcon,
        activePaths: [
          sitemap.applications(),
          sitemap.applications(ApplicationsTabEnumEnum.Current),
          sitemap.applications(ApplicationsTabEnumEnum.Contracts),
        ],
        activeQueryParam: 'application',
        count: unviewedApplicationCount,
      },
      ...(onboardingFinishedAt
        ? [
            {
              href: sitemap.profile(),
              label: t('pageTitles:profile'),
              icon: UserIcon,
            },
          ]
        : []),
      {
        href: sitemap.settings,
        label: t('pageTitles:settings'),
        icon: GearIcon,
      },
      ...(isMobile
        ? [
            {
              href: sitemap.observed,
              label: t('pageTitles:observed'),
              icon: HeartIcon,
            },
          ]
        : []),
    ],
    [
      sitemap,
      onboardingFinishedAt,
      t,
      isMobile,
      isApplicationParamPresent,
      unviewedApplicationCount,
    ],
  );

  return (
    <div
      className={mergeClasses(
        `fixed left-0 top-0 z-50 flex h-full-device w-full flex-col space-y-8 overflow-hidden bg-custom-gradient px-4 pb-11 pt-3 md:px-6 lg:left-4 lg:top-4 lg:h-[calc(100dvh-32px)] lg:max-w-[85px] lg:items-center lg:space-y-14 lg:rounded-2xl lg:bg-lightBlue lg:bg-none lg:px-2 lg:pt-8 lg:transition-none`,
        applyTransition ? 'transition-opacity' : '',
        isMobile && !open ? 'pointer-events-none opacity-0' : 'pointer-events-auto opacity-100',
      )}
    >
      <div className="flex items-center justify-between lg:justify-center">
        <Link className="hidden lg:block" to={sitemap.main}>
          <ConnectisSmallLogo className="w-[34px]" />
          {appConfig.environmentIndicatorLabel && appConfig.environmentIndicatorColor && (
            <div className="text-end" style={{ color: appConfig.environmentIndicatorColor }}>
              {appConfig.environmentIndicatorLabel}
            </div>
          )}
        </Link>
        <Link className="block lg:hidden" to={sitemap.main}>
          <ConnectisLogo className="w-[120px]" />
        </Link>
        <IconButton
          className="flex lg:hidden"
          name="closeNavbarButton"
          icon={CrossBigIcon}
          onClick={onClose}
        />
      </div>
      <UserBadge firstName={firstName} lastName={lastName} jobPositionId={jobPositionId} />
      <div className="flex h-full flex-col justify-between">
        <Menu paths={paths} onClose={onClose} />
        <div className="pt-4 lg:border-t lg:border-t-white">
          <SignOutButton
            onClick={() =>
              openModal('signOutModal', (closeModal) => <SignOutModal onClose={closeModal} />)
            }
          />
        </div>
      </div>
      <img
        className="pointer-events-none absolute left-0 top-0 -z-10 !m-0 h-full w-full lg:hidden"
        alt=""
        src={ellipseTopSrc}
      />
      <img
        className="pointer-events-none absolute bottom-0 left-0 right-0 top-0 -z-10 !m-auto h-full w-full lg:hidden"
        alt=""
        src={ellipseCenterSrc}
      />
    </div>
  );
});
